import { useState } from 'react';
import {
  Container,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Loader,
  Stack,
  Paper,
  Group,
  Text,
  Badge,
  ActionIcon,
  Modal,
  Select,
  Checkbox,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import {
  useMyDirectDeposit,
  useUpdateMyDirectDeposit,
  useDeleteMyDirectDeposit,
} from '@/hooks/usePortalApi';

interface DDAccount {
  id: number;
  bank_name: string;
  routing_number: string;
  account_number: string;
  account_type: string;
  allocation_type: string;
  allocation_amount: number | null;
  priority: number;
  is_remainder: boolean;
  prenote_status: string;
}

const REG_E_DISCLOSURE =
  'Under the Electronic Fund Transfer Act (Regulation E), you have the right to stop payment ' +
  'of a preauthorized electronic fund transfer by notifying your financial institution at least ' +
  'three business days before the scheduled date. You also have the right to receive documentation ' +
  'of electronic fund transfers and to dispute errors within 60 days of receiving your statement. ' +
  'Your liability for unauthorized transfers is limited if reported promptly.';

export default function DirectDeposit() {
  const { data, isLoading, error: loadError } = useMyDirectDeposit();
  const updateMutation = useUpdateMyDirectDeposit();
  const deleteMutation = useDeleteMyDirectDeposit();

  const [addOpen, setAddOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<DDAccount | null>(null);
  const [removeAccount, setRemoveAccount] = useState<DDAccount | null>(null);
  const [regEOpen, setRegEOpen] = useState(false);
  const [regEAccepted, setRegEAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [removePassword, setRemovePassword] = useState('');

  const accounts: DDAccount[] = data?.accounts ?? [];
  const isFirstEnrollment = accounts.length === 0;

  const form = useForm({
    initialValues: {
      bank_name: '',
      routing_number: '',
      account_number: '',
      account_type: 'checking',
      current_password: '',
      allocation_type: 'full',
      allocation_amount: '',
    },
    validate: {
      bank_name: (v) => (v.length > 0 ? null : 'Required'),
      routing_number: (v) => (/^\d{9}$/.test(v) ? null : 'Must be exactly 9 digits'),
      account_number: (v) => (/^\d{4,17}$/.test(v) ? null : 'Must be 4-17 digits'),
      current_password: (v) => (v.length > 0 ? null : 'Password is required'),
    },
  });

  const openAdd = () => {
    form.reset();
    setError(null);
    setSuccess(null);
    if (isFirstEnrollment) {
      setRegEOpen(true);
    } else {
      setAddOpen(true);
    }
  };

  const handleRegEAccept = () => {
    setRegEOpen(false);
    setRegEAccepted(true);
    setAddOpen(true);
  };

  const openEdit = (acct: DDAccount) => {
    form.setValues({
      bank_name: acct.bank_name,
      routing_number: '',
      account_number: '',
      account_type: acct.account_type,
      current_password: '',
      allocation_type: acct.allocation_type,
      allocation_amount: acct.allocation_amount?.toString() ?? '',
    });
    setEditAccount(acct);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (values: typeof form.values) => {
    setError(null);
    setSuccess(null);
    try {
      const payload: Record<string, any> = {
        bank_name: values.bank_name,
        routing_number: values.routing_number,
        account_number: values.account_number,
        account_type: values.account_type,
        current_password: values.current_password,
      };
      if (editAccount) {
        payload.config_id = editAccount.id;
      }
      if (isFirstEnrollment && regEAccepted) {
        payload.reg_e_consent = true;
      }
      await updateMutation.mutateAsync(payload);
      setAddOpen(false);
      setEditAccount(null);
      form.reset();
      setSuccess(
        editAccount
          ? 'Changes to bank details take effect next pay period.'
          : 'Direct deposit account added successfully.',
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleRemove = async () => {
    if (!removeAccount) return;
    setError(null);
    try {
      await deleteMutation.mutateAsync({
        configId: removeAccount.id,
        current_password: removePassword,
      });
      setRemoveAccount(null);
      setRemovePassword('');
      setSuccess('Direct deposit account removed.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove account');
    }
  };

  const prenoteColor = (status: string) => {
    if (status === 'verified') return 'green';
    if (status === 'failed') return 'red';
    return 'yellow';
  };

  if (isLoading) return <Loader mx="auto" mt="xl" />;

  return (
    <Container size="sm">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Direct Deposit</Title>
        <Button onClick={openAdd}>Add Account</Button>
      </Group>

      {loadError && (
        <Alert color="red" mb="md">
          Failed to load direct deposit accounts.
        </Alert>
      )}
      {error && (
        <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="green" mb="md" withCloseButton onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {accounts.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <Text c="dimmed">No direct deposit accounts configured.</Text>
          <Button mt="md" onClick={openAdd}>
            Set Up Direct Deposit
          </Button>
        </Paper>
      ) : (
        <Stack>
          {accounts.map((acct) => (
            <Paper key={acct.id} p="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Group gap="xs" mb={4}>
                    <Text fw={600}>{acct.bank_name || 'Unknown Bank'}</Text>
                    <Badge size="sm" variant="light">
                      {acct.account_type}
                    </Badge>
                    <Badge size="sm" color={prenoteColor(acct.prenote_status)} variant="light">
                      {acct.prenote_status}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Account: {acct.account_number} | Routing: {acct.routing_number}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Allocation: {acct.allocation_type}
                    {acct.allocation_amount ? ` ($${acct.allocation_amount})` : ''}
                    {acct.is_remainder ? ' (remainder)' : ''}
                  </Text>
                </div>
                <Group gap="xs">
                  <ActionIcon variant="subtle" onClick={() => openEdit(acct)}>
                    <IconEdit size={18} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => setRemoveAccount(acct)}>
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Reg E Disclosure Modal */}
      <Modal
        opened={regEOpen}
        onClose={() => setRegEOpen(false)}
        title="Electronic Fund Transfer Authorization"
        size="lg"
      >
        <Stack>
          <Text size="sm">{REG_E_DISCLOSURE}</Text>
          <Checkbox
            label="I have read and agree to the Electronic Fund Transfer Act disclosure"
            checked={regEAccepted}
            onChange={(e) => setRegEAccepted(e.currentTarget.checked)}
          />
          <Button disabled={!regEAccepted} onClick={handleRegEAccept}>
            Accept & Continue
          </Button>
        </Stack>
      </Modal>

      {/* Add / Edit Modal */}
      <Modal
        opened={addOpen || !!editAccount}
        onClose={() => {
          setAddOpen(false);
          setEditAccount(null);
          form.reset();
        }}
        title={editAccount ? 'Edit Direct Deposit' : 'Add Direct Deposit Account'}
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Bank Name"
              required
              {...form.getInputProps('bank_name')}
            />
            <TextInput
              label="Routing Number"
              required
              maxLength={9}
              placeholder={editAccount ? 'Re-enter for security' : '9-digit routing number'}
              {...form.getInputProps('routing_number')}
            />
            <TextInput
              label="Account Number"
              required
              placeholder={editAccount ? 'Re-enter for security' : 'Account number'}
              {...form.getInputProps('account_number')}
            />
            <Select
              label="Account Type"
              data={[
                { value: 'checking', label: 'Checking' },
                { value: 'savings', label: 'Savings' },
              ]}
              {...form.getInputProps('account_type')}
            />
            <PasswordInput
              label="Current Password"
              description="Required for security verification"
              required
              {...form.getInputProps('current_password')}
            />
            {error && <Alert color="red">{error}</Alert>}
            <Button type="submit" loading={updateMutation.isPending}>
              {editAccount ? 'Save Changes' : 'Add Account'}
            </Button>
          </Stack>
        </form>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        opened={!!removeAccount}
        onClose={() => {
          setRemoveAccount(null);
          setRemovePassword('');
        }}
        title="Remove Direct Deposit Account"
      >
        <Stack>
          <Text>
            Remove <strong>{removeAccount?.bank_name}</strong> ({removeAccount?.account_number})?
          </Text>
          <PasswordInput
            label="Confirm your password"
            value={removePassword}
            onChange={(e) => setRemovePassword(e.currentTarget.value)}
            required
          />
          {error && <Alert color="red">{error}</Alert>}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setRemoveAccount(null);
                setRemovePassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              disabled={!removePassword}
              onClick={handleRemove}
            >
              Remove Account
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
